import { useState, useMemo } from 'react'
import { RefreshCw, Edit, Trash2, FolderOpen, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { Projeto } from '../tipos/projeto'
import { projetoService } from '../services/projeto'
import { useMutationWithToast } from '../../../hooks/useMutationWithToast'
import { ConfirmDialog } from '../../../componentesCompartilhados/ui/ConfirmDialog'
import { formatDateToBrazilian } from '../../../utils/formatUtils'

interface ProjetoListProps {
  projetos: Projeto[]
  isLoading: boolean
  onRefresh: () => void
  onEditProject: (projeto: Projeto) => void
}

export function ProjetoList({ projetos, isLoading, onRefresh, onEditProject }: ProjetoListProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [showConfirmExcluir, setShowConfirmExcluir] = useState(false)
  const [projetoParaExcluir, setProjetoParaExcluir] = useState<string | null>(null)
  const itemsPerPage = 5
  
  // Tamanhos padronizados para ícones
  const ICON_SIZES = {
    small: 'w-4 h-4',
    medium: 'w-5 h-5',
    large: 'w-6 h-6',
    xlarge: 'w-8 h-8',
    xxlarge: 'w-16 h-16'
  }
  
  // Filtrar projetos baseado no termo de busca
  const filteredProjetos = useMemo(() => {
    if (!searchTerm.trim()) {
      return projetos
    }
    
    const term = searchTerm.toLowerCase().trim()
    return projetos.filter((projeto) => {
      const projetoData = projeto as any
      const nome = (projetoData.nome || projetoData.Nome || '').toLowerCase()
      const numeroTicket = (projetoData.numeroTicket || projetoData.NumeroTicket || '').toLowerCase()
      
      return nome.includes(term) || numeroTicket.includes(term)
    })
  }, [projetos, searchTerm])
  
  // Calcular paginação
  const totalPages = Math.ceil(filteredProjetos.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentProjetos = filteredProjetos.slice(startIndex, endIndex)

  const deleteMutation = useMutationWithToast({
    mutationFn: projetoService.excluirProjeto,
    successMessage: 'Projeto excluído com sucesso!',
    errorMessage: 'Erro ao excluir projeto',
    invalidateQueries: ['projetos'],
  })

  const handleDelete = (id: string) => {
    setProjetoParaExcluir(id)
    setShowConfirmExcluir(true)
  }

  const confirmarExcluirProjeto = () => {
    if (projetoParaExcluir) {
      deleteMutation.mutate(projetoParaExcluir)
      setShowConfirmExcluir(false)
      setProjetoParaExcluir(null)
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1) // Reset para primeira página quando buscar
  }

  const clearSearch = () => {
    setSearchTerm('')
    setCurrentPage(1)
  }

  const getStatusConfig = (statusCodigo: string) => {
    const configs: Record<string, { text: string; class: string }> = {
      'ESC': { text: 'Escopo', class: 'bg-warning-100 text-warning-800' },
      'ORC': { text: 'Orçamento', class: 'bg-brand-100 text-brand-800' },
      'DES': { text: 'Desenvolvimento', class: 'bg-success-100 text-success-800' },
      'TES': { text: 'Testes Internos', class: 'bg-info-100 text-info-800' },
      'TPR': { text: 'Testes de Produto', class: 'bg-info-100 text-info-800' },
      'HOM': { text: 'Homologação', class: 'bg-purple-100 text-purple-800' },
      'PUB': { text: 'Publicado', class: 'bg-green-100 text-green-800' },
      'CAN': { text: 'Cancelado', class: 'bg-red-100 text-red-800' },
      'PAU': { text: 'Pausado', class: 'bg-gray-100 text-gray-800' },
      'TER': { text: 'Aguardando Terceiros', class: 'bg-yellow-100 text-yellow-800' },
      'BLO': { text: 'Backlog', class: 'bg-gray-100 text-gray-800' },
    }
    return configs[statusCodigo] || { text: 'Desconhecido', class: 'bg-gray-100 text-gray-800' }
  }

  const getPriorityConfig = (prioridadeCodigo: string) => {
    const configs: Record<string, { text: string; class: string }> = {
      'ALT': { text: 'Alta', class: 'text-red-600' },
      'MED': { text: 'Média', class: 'text-yellow-600' },
      'BAI': { text: 'Baixa', class: 'text-green-600' },
      'MAX': { text: 'Máxima', class: 'text-red-800 font-bold' },
    }
    return configs[prioridadeCodigo] || { text: 'Não definida', class: 'text-gray-600' }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando projetos...</p>
          <p className="text-sm text-gray-500">Aguarde enquanto buscamos os dados</p>
        </div>
      </div>
    )
  }

  if (projetos.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <FolderOpen className={`${ICON_SIZES.xxlarge} text-gray-300 mx-auto mb-4`} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum projeto encontrado</h3>
          <p className="text-gray-600">Não há projetos que correspondam aos seus critérios de busca.</p>
        </div>
      </div>
    )
  }

  if (filteredProjetos.length === 0 && searchTerm.trim()) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <Search className={`${ICON_SIZES.xxlarge} text-gray-300 mx-auto mb-4`} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum resultado encontrado</h3>
          <p className="text-gray-600 mb-4">
            Não encontramos projetos que correspondam ao termo "{searchTerm}".
          </p>
          <button 
            onClick={clearSearch}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Limpar Busca
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-success-500 to-brand-500 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
              <FolderOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Lista de Projetos</h3>
              <p className="text-sm text-gray-600">
                {searchTerm.trim() ? `${filteredProjetos.length} de ${projetos.length} projetos` : `${projetos.length} projetos`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Campo de busca */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar projetos..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-64 pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  title="Limpar busca"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <button
              onClick={onRefresh}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              title="Atualizar"
            >
              <RefreshCw className={ICON_SIZES.small} />
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center space-x-1">
                  <span>Projeto</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center space-x-1">
                  <span>Prioridade</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center space-x-1">
                  <span>Data Recebimento</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                AÇÕES
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentProjetos.map((projeto) => {
              // Extrair dados do backend (Pascal Case) ou frontend (camelCase)
              const projetoData = projeto as any
              const nome = projetoData.nome || projetoData.Nome || ''
              const descricao = projetoData.descricao || projetoData.Descricao
              const statusCodigo = projetoData.statusCodigo || projetoData.StatusCodigo || ''
              const prioridadeCodigo = projetoData.prioridadeCodigo || projetoData.PrioridadeCodigo || ''
              const statusNome = projetoData.statusNome || projetoData.StatusNome
              const prioridadeNome = projetoData.prioridadeNome || projetoData.PrioridadeNome
              const dataRecebimento = projetoData.dataRecebimento || projetoData.DataRecebimento
              const id = projetoData.id

              // Usar o nome do backend se disponível, senão tentar converter o código
              const statusConfig = statusNome 
                ? { text: statusNome, class: getStatusConfig(statusCodigo).class }
                : getStatusConfig(statusCodigo)
              const priorityConfig = prioridadeNome
                ? { text: prioridadeNome, class: getPriorityConfig(prioridadeCodigo).class }
                : getPriorityConfig(prioridadeCodigo)
              
              // Formatar data
              let dataFormatada = 'Data inválida'
              if (dataRecebimento) {
                dataFormatada = formatDateToBrazilian(dataRecebimento) || 'Data inválida'
              }

              return (
                <tr key={id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
                        <FolderOpen className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900">{nome}</h4>
                        <p className="text-sm text-gray-600 truncate max-w-xs">
                          {descricao || 'Sem descrição'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.class}`}>
                      {statusConfig.text}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-medium ${priorityConfig.class}`}>
                      {priorityConfig.text}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{dataFormatada}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onEditProject(projeto)
                        }}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Editar"
                      >
                        <Edit className={ICON_SIZES.small} />
                      </button>
                      <button
                        onClick={() => handleDelete(id.toString())}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Excluir"
                        disabled={deleteMutation.isLoading}
                      >
                        <Trash2 className={ICON_SIZES.small} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Mostrando {startIndex + 1} a {Math.min(endIndex, filteredProjetos.length)} de {filteredProjetos.length} projetos
              {searchTerm.trim() && (
                <span className="text-gray-500"> (filtrados de {projetos.length} total)</span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Página anterior"
              >
                <ChevronLeft className={ICON_SIZES.small} />
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 ${
                      currentPage === page
                        ? 'bg-brand-500 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Próxima página"
              >
                <ChevronRight className={ICON_SIZES.small} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog para exclusão de projeto */}
      {projetoParaExcluir && (
        <ConfirmDialog
          isOpen={showConfirmExcluir}
          onClose={() => {
            setShowConfirmExcluir(false)
            setProjetoParaExcluir(null)
          }}
          onConfirm={confirmarExcluirProjeto}
          title="Confirmar Exclusão"
          message="Tem certeza que deseja excluir este projeto?\n\nEsta ação não pode ser desfeita."
          confirmText="Excluir"
          cancelText="Cancelar"
          variant="danger"
        />
      )}
    </div>
  )
}

